import React from "react";
import {Card, CardBody} from "reactstrap";
import {formatMoney, formatPercent} from "../../../shared/utils/format";

// Presentation shared by the order form and the order detail page, so the two
// look the same. Each "Summary" renders the inside of a list row; the caller
// provides the row wrapper (and any actions).

/**
 * `cost` (the unit cost the line was priced with) is for the provider's own views: the customer's never gets it.
 * @param {{name?: string, quantity: number, unit: number, cost?: number, total: number, currency?: string, errors?: string[]}} props
 */
export const ItemSummary = ({name, quantity, unit, cost, total, currency, errors = []}) => (
    <>
        <div className="flex-grow-1" style={{minWidth: 0}}>
            <div className="fw-semibold text-break">{name}</div>
            <div className="small text-muted">
                {quantity} × {formatMoney(unit, currency)}
            </div>
            {cost !== undefined && (
                <div className="small text-muted">
                    Custo: {quantity} × {formatMoney(cost, currency)}
                </div>
            )}
            {errors.map((message) => (
                <div className="small text-danger" key={message}>
                    {message}
                </div>
            ))}
        </div>
        <div className="text-nowrap pt-1">{formatMoney(total, currency)}</div>
    </>
);

/** @param {{typeName?: string, description: string, fields: Record<string, string>, errors?: string[]}} props */
export const DeviceSummary = ({typeName, description, fields, errors = []}) => {
    const filled = Object.entries(fields).filter(([, v]) => v);
    return (
        <div className="flex-grow-1" style={{minWidth: 0}}>
            <div className="fw-semibold text-break">{typeName || "Sem tipo"}</div>
            <div className="text-break">{description}</div>
            {filled.length > 0 && (
                <div className="small text-muted text-break">{filled.map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>
            )}
            {errors.map((message) => (
                <div className="small text-danger" key={message}>
                    {message}
                </div>
            ))}
        </div>
    );
};

/** @param {{type: "VAL"|"PER", value: number, description?: string, amount: number, currency?: string, errors?: string[]}} props */
export const DiscountSummary = ({type, value, description, amount, currency, errors = []}) => (
    <>
        <div className="flex-grow-1" style={{minWidth: 0}}>
            <div className="fw-semibold">{type === "PER" ? `${value}%` : formatMoney(value, currency)}</div>
            {description && <div className="text-break">{description}</div>}
            {errors.map((message) => (
                <div className="small text-danger" key={message}>
                    {message}
                </div>
            ))}
        </div>
        <div className="text-nowrap pt-1">-{formatMoney(amount, currency)}</div>
    </>
);

/**
 * `cost` is what the order cost the provider and `margin` what it earned them, for their own views only
 * (null or left out: not shown).
 * @param {{subtotal: number, discount: number, total: number, cost?: number|null, margin?: {amount: number, percent: number|null}|null, currency?: string, totalLabel: string}} props
 */
export const TotalsCard = ({subtotal, discount, total, cost, margin, currency, totalLabel}) => (
    <Card className="mb-3 bg-light">
        <CardBody>
            <div className="d-flex justify-content-between">
                <span>Subtotal</span>
                <span>{formatMoney(subtotal, currency)}</span>
            </div>
            {discount > 0 && (
                <div className="d-flex justify-content-between text-muted">
                    <span>Desconto</span>
                    <span>-{formatMoney(discount, currency)}</span>
                </div>
            )}
            <div className="d-flex justify-content-between fw-bold fs-5 mt-1">
                <span>{totalLabel}</span>
                <span>{formatMoney(total, currency)}</span>
            </div>
            {cost !== undefined && cost !== null && (
                <div className="d-flex justify-content-between small text-muted mt-1">
                    <span>Custo total</span>
                    <span>{formatMoney(cost, currency)}</span>
                </div>
            )}
            {margin && (
                <div className="d-flex justify-content-between small text-muted">
                    <span>Margem</span>
                    <span>
                        {formatMoney(margin.amount, currency)}
                        {margin.percent !== null && ` (${formatPercent(margin.percent)})`}
                    </span>
                </div>
            )}
        </CardBody>
    </Card>
);
